// Cleanability scoring helper — mirrors backend/services/cleanabilityAssessment.js.
// The server recomputes the score on save; this copy drives the live preview in
// the "Help me rate" dialog and the printed assessment sheet.

export const CLEANABILITY_CONFIG_KEY = 'cleanability_assessment_config';
export const CLEANABILITY_RATINGS = ['Easy', 'Medium', 'Difficult'];

export const DEFAULT_CLEANABILITY_CONFIG = {
  questions: [
    {
      key: 'solubility_in_agent',
      label: 'Solubility of the residue in the cleaning agent actually used',
      options: [
        { label: 'Freely soluble', score: 1 },
        { label: 'Sparingly soluble', score: 2 },
        { label: 'Poorly soluble or insoluble', score: 3 },
      ],
    },
    {
      key: 'physical_nature',
      label: 'Physical nature of the residue',
      options: [
        { label: 'Free-flowing powder, not sticky', score: 1 },
        { label: 'Slightly sticky or hygroscopic', score: 2 },
        { label: 'Sticky, waxy, oily or tarry', score: 3 },
      ],
    },
    {
      key: 'colour_stain',
      label: 'Leaves colour or stain on equipment surfaces',
      options: [
        { label: 'No colour or stain', score: 1 },
        { label: 'Faint colour that rinses off', score: 2 },
        { label: 'Strong colour or stains surfaces', score: 3 },
      ],
    },
    {
      key: 'heat_dry_effect',
      label: 'Effect of heat, drying or dirty hold time',
      options: [
        { label: 'No change', score: 1 },
        { label: 'Hardens or cakes', score: 2 },
        { label: 'Chars, polymerises or bakes on', score: 3 },
      ],
    },
    {
      key: 'cleaning_agent_needed',
      label: 'Cleaning agent needed to remove it',
      options: [
        { label: 'Water alone', score: 1 },
        { label: 'Mild detergent', score: 2 },
        { label: 'Solvent or strong alkaline/acid detergent', score: 3 },
      ],
    },
    {
      key: 'failure_history',
      label: 'Cleaning failure history (failed visual checks, OOS swab/rinse results, re-cleans)',
      options: [
        { label: 'None', score: 1 },
        { label: 'One event', score: 2 },
        { label: 'Repeated events', score: 3 },
      ],
    },
  ],
  bands: { easy_max: 9, medium_max: 13 },
};

export const ratingForTotal = (total, bands) => {
  if (total <= Number(bands.easy_max)) return 'Easy';
  if (total <= Number(bands.medium_max)) return 'Medium';
  return 'Difficult';
};

// answers: { [questionKey]: optionIndex }
export const scoreAnswers = (config, answers = {}) => {
  let total = 0;
  let max = 0;
  let min = 0;
  let answered = 0;
  config.questions.forEach((question) => {
    const scores = question.options.map((o) => Number(o.score) || 0);
    max += Math.max(...scores);
    min += Math.min(...scores);
    const option = question.options[Number(answers[question.key])];
    if (answers[question.key] !== undefined && answers[question.key] !== '' && option) {
      total += Number(option.score) || 0;
      answered += 1;
    }
  });
  const complete = answered === config.questions.length;
  return { total, min, max, answered, complete, suggested: complete ? ratingForTotal(total, config.bands) : null };
};

// Stored record (api_master.cleanability_assessment) or pending submission → { [questionKey]: optionIndex }
export const answersFromRecord = (record) => Object.fromEntries(
  Object.entries(record?.answers || {}).map(([key, value]) => [key, value && typeof value === 'object' ? value.option_index : value])
);

// One checklist model for every export: a blank sheet (no record), a live
// preview from the dialog (answers are option indexes) or a saved record
// (answers are { option_index, answer, score, options? } snapshots).
export const buildChecklist = (config, record) => {
  const answers = record?.answers || {};
  const saved = Object.values(answers).some((a) => a && typeof a === 'object');
  const configByKey = Object.fromEntries((config?.questions || []).map((q) => [q.key, q]));
  const rows = saved
    ? Object.entries(answers).map(([key, a]) => {
      const sameQuestion = configByKey[key]?.label === a.question ? configByKey[key] : null;
      // Older records have no options snapshot; fall back to the config only if the question is unchanged
      const options = a.options || sameQuestion?.options || [{ label: a.answer, score: a.score }];
      const selected = a.options || sameQuestion ? Number(a.option_index) : 0;
      return { question: a.question, options, selected };
    })
    : (config?.questions || []).map((q) => ({
      question: q.label,
      options: q.options,
      selected: answers[q.key] === undefined || answers[q.key] === '' ? null : Number(answers[q.key]),
    }));
  const scored = rows.map((r) => {
    const options = r.options.map((o, oi) => ({ label: o.label, score: Number(o.score) || 0, checked: r.selected === oi }));
    return { question: r.question, options, score: options.find((o) => o.checked)?.score ?? null };
  });
  const answered = scored.filter((r) => r.score !== null).length;
  const complete = scored.length > 0 && answered === scored.length;
  const total = scored.reduce((sum, r) => sum + (r.score || 0), 0);
  const max = scored.reduce((sum, r) => sum + Math.max(0, ...r.options.map((o) => o.score)), 0);
  const bands = record?.bands || config?.bands || DEFAULT_CLEANABILITY_CONFIG.bands;
  const suggested = record?.suggested || (complete ? ratingForTotal(total, bands) : '');
  const finalRating = record?.final_rating || '';
  return {
    rows: scored,
    answered,
    total: answered ? total : null,
    max,
    bands,
    suggested,
    final_rating: finalRating,
    overridden: !!(finalRating && suggested && finalRating !== suggested),
    override_reason: record?.override_reason || '',
    assessed_by_name: record?.assessed_by_name || '',
    assessed_at: record?.assessed_at || null,
  };
};

const esc = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const blankLine = '<span style="display:inline-block;min-width:120px;border-bottom:1px solid #333">&nbsp;</span>';

// Printable checklist (print / save as PDF). subject = { heading, rows: [[label, value]] }
// names what is rated: an API, or one API inside a product (product override).
// With no record it prints blank, for completing by hand.
export const cleanabilityChecklistHTML = (subject, config, record) => {
  const c = buildChecklist(config, record);
  const rows = c.rows.map((r, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${esc(r.question)}</td>
      <td>${r.options.map((o) => `<div class="opt">${o.checked ? '&#9745;' : '&#9744;'} ${esc(o.label)} <span class="sc">(${esc(o.score)})</span></div>`).join('')}</td>
      <td style="text-align:center">${r.score === null ? '' : esc(r.score)}</td>
    </tr>`).join('');
  return `<style>
    .ca h3 { font-size: 13px; margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #999; }
    .ca table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 11px; }
    .ca th, .ca td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
    .ca th { background: #f0f0f0; font-weight: 600; }
    .ca .kv th { width: 28%; }
    .ca .opt { margin: 1px 0; font-size: 11px; }
    .ca .sc { color: #555; font-size: 10px; }
    .ca .outcome { border: 1.5px solid #333; padding: 8px 10px; margin: 8px 0 10px; font-size: 13px; font-weight: 700; }
    .ca .note { font-size: 10px; color: #444; margin-top: 6px; }
  </style>
  <div class="ca">
    <h3>${esc(subject?.heading || 'Subject')}</h3>
    <table class="kv">${(subject?.rows || []).map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v || '—')}</td></tr>`).join('')}</table>
    <h3>Cleanability rating checklist</h3>
    <p class="note" style="margin-top:0">Tick one answer per question for the residue as it behaves on your equipment with your cleaning process. The score of each answer is shown in brackets.</p>
    <table>
      <tr><th style="width:24px">#</th><th style="width:38%">Question</th><th>Answer</th><th style="width:50px">Score</th></tr>
      ${rows}
      <tr><th colspan="3" style="text-align:right">Total</th><th style="text-align:center">${c.total === null ? '' : esc(c.total)} / ${esc(c.max)}</th></tr>
    </table>
    <p class="note">Rating bands: total ≤ ${esc(c.bands.easy_max)} → Easy; ≤ ${esc(c.bands.medium_max)} → Medium; above → Difficult.</p>
    <div class="outcome">Suggested: ${c.suggested ? esc(c.suggested) : blankLine} &nbsp;|&nbsp; Final rating: ${c.final_rating ? `${esc(c.final_rating)}${c.overridden ? ' (overridden)' : ''}` : blankLine}</div>
    <table class="kv"><tr><th>Reason if final ≠ suggested</th><td style="height:28px">${esc(c.override_reason)}</td></tr></table>
    <h3>Sign-off</h3>
    <table>
      <tr><th style="width:22%">Role</th><th>Name</th><th style="width:26%">Date and time</th><th style="width:22%">Signature</th></tr>
      <tr><td>Assessed by</td><td>${esc(c.assessed_by_name)}</td><td>${c.assessed_at ? esc(new Date(c.assessed_at).toLocaleString()) : ''}</td><td></td></tr>
      <tr><td>Reviewed by (QA)</td><td></td><td></td><td></td></tr>
    </table>
    <p class="note">${c.answered ? '' : 'Blank checklist for completion by hand. '}This is a documented assessment of cleanability based on known residue behaviour. It is not a substitute for a lab cleanability (coupon) study where one is required by SOP.</p>
  </div>`;
};

// Same checklist as an Excel sheet: one row per answer option, chosen ones ticked.
export const exportCleanabilityChecklistXlsx = async (subject, config, record, fileName) => {
  const XLSX = await import('xlsx');
  const c = buildChecklist(config, record);
  const aoa = [
    ['Cleanability Rating Checklist'],
    [],
    ...(subject?.rows || []).map(([k, v]) => [k, v || '']),
    [],
    ['#', 'Question', 'Answer option', 'Option score', 'Selected', 'Score'],
  ];
  c.rows.forEach((r, i) => r.options.forEach((o, oi) => aoa.push([
    oi === 0 ? i + 1 : '',
    oi === 0 ? r.question : '',
    o.label,
    o.score,
    o.checked ? '✓' : '',
    oi === 0 && r.score !== null ? r.score : '',
  ])));
  aoa.push(
    [],
    ['', 'Total', c.total === null ? '' : `${c.total} / ${c.max}`],
    ['', 'Rating bands', `≤ ${c.bands.easy_max} Easy; ≤ ${c.bands.medium_max} Medium; above Difficult`],
    ['', 'Suggested rating', c.suggested || ''],
    ['', 'Final rating', c.final_rating ? `${c.final_rating}${c.overridden ? ' (overridden)' : ''}` : ''],
    ['', 'Reason if final ≠ suggested', c.override_reason],
    ['', 'Assessed by', c.assessed_by_name],
    ['', 'Assessed at', c.assessed_at ? new Date(c.assessed_at).toLocaleString() : ''],
    ['', 'Reviewed by (QA)', ''],
  );
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  sheet['!cols'] = [{ wch: 5 }, { wch: 55 }, { wch: 42 }, { wch: 12 }, { wch: 10 }, { wch: 10 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Checklist');
  XLSX.writeFile(book, `${String(fileName || 'Cleanability_Checklist').replace(/[^\w.-]+/g, '_')}.xlsx`);
};

// API Master assessment sheet.
export const cleanabilityAssessmentReportHTML = (api, record, config = DEFAULT_CLEANABILITY_CONFIG) => cleanabilityChecklistHTML({
  heading: 'API',
  rows: [['API Code', api?.api_code], ['API Name', api?.api_name], ['Solubility Classification', api?.solubility_classification]],
}, config, record);
